import React, { FC } from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy: FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="/logo_text.png" alt="Code Coast" className="h-8" />
            </Link>
            <Link
              to="/"
              className="text-[#14b7cd] uppercase text-sm px-6 py-2 border border-[#14b7cd] hover:bg-[#14b7cd] hover:text-white transition-colors duration-300"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              Privacy Policy
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                This Privacy Policy describes how Code Coast ("we", "our", or "us") collects, 
                uses, and protects your personal information when you use our educational platform 
                and services. We are committed to protecting your privacy and ensuring the security 
                of your personal data.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Effective Date:</strong> This Privacy Policy is effective as of {new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })} and will remain in effect except with respect to any changes in its provisions in the future.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Personal Information</h3>
              <p className="text-gray-700 mb-4">
                We collect information you provide directly to us, such as when you:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700">
                <li>Create an account or profile</li>
                <li>Enroll in courses or programs</li>
                <li>Make payments or subscriptions</li>
                <li>Contact us for support</li>
                <li>Participate in surveys or feedback</li>
                <li>Communicate with instructors or other students</li>
              </ul>
              
              <p className="text-gray-700 mb-4">This information may include:</p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Name, email address, and contact information</li>
                <li>Payment and billing information</li>
                <li>Educational background and interests</li>
                <li>Profile picture and biographical information</li>
                <li>Course progress and completion data</li>
                <li>Messages and communications</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Automatically Collected Information</h3>
              <p className="text-gray-700 mb-4">
                We automatically collect certain information about your use of our services:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Device information (type, operating system, browser)</li>
                <li>IP address and location data</li>
                <li>Usage patterns and preferences</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Log files and analytics data</li>
                <li>Video viewing and interaction data</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Provide, maintain, and improve our educational services</li>
                <li>Process enrollments, payments, and subscriptions</li>
                <li>Track your progress and provide personalized learning experiences</li>
                <li>Communicate with you about courses, updates, and support</li>
                <li>Send you important notices and administrative messages</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Conduct research and analytics to improve our services</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or rent your personal information to third parties. 
                We may share your information in the following circumstances:
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with trusted third-party service providers who assist us in:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700">
                <li>Payment processing and billing</li>
                <li>Email and communication services</li>
                <li>Analytics and data analysis</li>
                <li>Cloud storage and hosting</li>
                <li>Customer support</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Legal Requirements</h3>
              <p className="text-gray-700 mb-8">
                We may disclose your information if required by law or in response to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Court orders or legal processes</li>
                <li>Government requests or investigations</li>
                <li>Protection of our rights and property</li>
                <li>Safety of our users and the public</li>
                <li>Prevention of fraud or illegal activities</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure servers and databases</li>
                <li>Regular security audits and assessments</li>
                <li>Access controls and authentication</li>
                <li>Staff training on data protection</li>
                <li>Incident response procedures</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
              <p className="text-gray-700 mb-8">
                We retain your personal information for as long as necessary to provide our services 
                and fulfill the purposes outlined in this Privacy Policy. We will delete or anonymize 
                your information when it is no longer needed, unless we are required to retain it 
                for legal or regulatory purposes.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
                <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
              </ul>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Exercise Your Rights:</strong> To exercise any of these rights, please contact us at 
                      <a href="mailto:privacy@codecoast.org" className="text-yellow-700 underline"> privacy@codecoast.org</a> 
                      or use our <Link to="/deletion-instructions" className="text-yellow-700 underline">data deletion instructions</Link>.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar technologies to enhance your experience on our platform:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-8 text-gray-700">
                <li><strong>Essential Cookies:</strong> Required for basic platform functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use our services</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Marketing Cookies:</strong> Used for targeted advertising (with your consent)</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
              <p className="text-gray-700 mb-8">
                Our platform may integrate with third-party services such as payment processors, 
                analytics providers, and social media platforms. These services have their own 
                privacy policies, and we encourage you to review them.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
              <p className="text-gray-700 mb-8">
                Your information may be transferred to and processed in countries other than your own. 
                We ensure appropriate safeguards are in place to protect your data in accordance 
                with applicable privacy laws.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 mb-8">
                Our services are not intended for children under 13 years of age. We do not knowingly 
                collect personal information from children under 13. If we become aware that we have 
                collected such information, we will take steps to delete it promptly.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-8">
                We may update this Privacy Policy from time to time. We will notify you of any 
                material changes by posting the new Privacy Policy on this page and updating 
                the "Effective Date" at the top. Your continued use of our services after 
                such changes constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Email:</strong> <a href="mailto:privacy@codecoast.org" className="text-[#14b7cd] hover:underline">privacy@codecoast.org</a></li>
                  <li><strong>Support:</strong> <a href="mailto:support@codecoast.org" className="text-[#14b7cd] hover:underline">support@codecoast.org</a></li>
                  <li><strong>Phone:</strong> +233 24 086 5997</li>
                  <li><strong>Address:</strong> Ekumfi Essuehyia, Ekumfiman, Ghana</li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      <strong>Your Privacy Matters:</strong> We are committed to protecting your privacy and being 
                      transparent about our data practices. If you have any concerns or questions, 
                      please don't hesitate to reach out to us.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center pt-8 border-t border-gray-200">
                <p className="text-gray-500 text-sm">
                  Last updated: {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} Code Coast. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
